import React from 'react';
import { motion } from 'motion/react';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { LayoutDashboard, Calculator, FileText, Users, Settings } from 'lucide-react';
import OrgSwitcher from '@/lib/components/common/navbar/OrgSwitcher';

// Navigation items for Energy CRM
const navigationItems = [
  {
    icon: LayoutDashboard,
    label: 'NAERAS Υβριδικό',
    path: '/naeras',
    description: 'Διαχείριση προσφορών',
  },
  {
    icon: FileText,
    label: 'Files',
    path: '/files',
    description: 'Manage documents and files',
  },
  {
    icon: Users,
    label: 'Personnel',
    path: '/personnel',
    description: 'Manage team members',
  },
  {
    icon: Settings,
    label: 'Settings',
    path: '/settings',
    description: 'Account preferences',
  },
];
import { Shield } from 'lucide-react';
import { useSelector } from 'react-redux';
import { getCurrentSessionUser } from '@/data/store/selectors';

interface NavbarContentProps {
  onItemClick: () => void;
  pathname: string;
}

const NavbarContent = ({ onItemClick, pathname }: NavbarContentProps) => {
  const user = useSelector(getCurrentSessionUser);
  return (
    <div className="space-y-6">
      {/* Active organization / project */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="border-b border-gray-200 pb-4"
      >
        <OrgSwitcher onNavigate={onItemClick} />
      </motion.div>

      {/* Navigation Items */}
      <nav className="space-y-2">
        {navigationItems.map((item, index) => (
          <motion.div
            key={item.path}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
          >
            <Link
              to={item.path}
              onClick={onItemClick}
              className={cn(
                'group flex items-center gap-4 rounded-xl p-3 transition-all duration-200',
                pathname === item.path || pathname.startsWith(item.path)
                  ? 'bg-blue-50 text-blue-700 shadow-sm'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
              )}
            >
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
                  pathname === item.path || pathname.startsWith(item.path)
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200',
                )}
              >
                <item.icon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="font-medium">{item.label}</div>
                <div className="text-sm text-gray-500">{item.description}</div>
              </div>
            </Link>
          </motion.div>
        ))}
      </nav>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.4 }}
        className="border-t border-gray-200 pt-4"
      >
        <h3 className="mb-3 text-sm font-medium text-gray-900">Quick Actions</h3>
        <div className="grid grid-cols-2 gap-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="rounded-lg border border-gray-200 bg-white p-3 text-left transition-all hover:border-gray-300 hover:shadow-sm"
          >
            <div className="text-sm font-medium text-gray-900">Add System</div>
            <div className="text-xs text-gray-500">New energy system</div>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="rounded-lg border border-gray-200 bg-white p-3 text-left transition-all hover:border-gray-300 hover:shadow-sm"
          >
            <div className="text-sm font-medium text-gray-900">Generate Report</div>
            <div className="text-xs text-gray-500">Energy analytics</div>
          </motion.button>
        </div>
      </motion.div>

      {/* Mode Switch (Admin / Normal) */}
      {user && (user as any).developer === true && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
          className="border-t border-gray-200 pt-4"
        >
          <Link
            to="/admin"
            onClick={onItemClick}
            className={cn(
              'group flex items-center gap-4 rounded-xl p-3 transition-all duration-200',
              pathname.startsWith('/admin')
                ? 'bg-purple-50 text-purple-700 shadow-sm border border-purple-200'
                : 'text-gray-700 hover:text-purple-700 hover:bg-purple-50/50 border border-transparent hover:border-purple-100',
            )}
          >
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
                pathname.startsWith('/admin')
                  ? 'bg-purple-200 text-purple-700'
                  : 'bg-gray-100 text-gray-500 group-hover:bg-purple-100 group-hover:text-purple-600',
              )}
            >
              <Shield className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="font-medium">Admin Mode</div>
              <div className="text-sm text-gray-500 group-hover:text-purple-500">CAS Dashboard</div>
            </div>
          </Link>
        </motion.div>
      )}
    </div>
  );
};

export default NavbarContent;
